import Phaser from 'phaser';
import type { CameraConfig, InputConfig } from '../config/gameConfig';
import type { ScreenPoint } from '../core/types';
import type { ActionBus, PointerKind } from './actions';
import { PinchGesture } from './PinchGesture';
import { PointerGesture } from './PointerGesture';

/** Does a touch press at this screen point grab the placement preview (instead of panning)? */
export type DragCapture = (screen: ScreenPoint) => boolean;
/** Should a drag that starts now draw a selection box instead of panning? (`shift` = Shift held with a mouse) */
export type BoxCapture = (shift: boolean) => boolean;

/**
 * Pointer adapter for mouse, touch and pen (Phaser delivers all three as
 * pointers). Converts raw events into GameActions:
 *
 *   press + release without moving → primary   (left button / tap)
 *   press + move past threshold    → pan       (or placementDrag when a touch starts on the build preview)
 *   right-button click             → secondary
 *   mouse move                     → hover     (touch has no hover)
 *   wheel                          → zoom at the cursor
 *   two fingers                    → pinch     (zoom about the fingers + pan with them)
 *
 * A second finger cancels the first finger's tap / pan and starts a pinch.
 * After a pinch, the remaining finger does nothing until every finger is up,
 * so lifting fingers one by one never produces a stray tap or jump.
 */
export class PointerInput {
  private readonly gesture: PointerGesture;
  private readonly touchThreshold: number;
  private readonly pinch = new PinchGesture();
  private activePointerId: number | null = null;
  private activeKind: PointerKind = 'mouse';
  private grabbedPreview = false;
  private boxing = false;
  private boxFrom: ScreenPoint = { x: 0, y: 0 };
  /** Touches currently down (ids), for "wait until all fingers are up" after a pinch. */
  private readonly touches = new Set<number>();
  private suppressUntilAllUp = false;
  private lastPosition: ScreenPoint | null = null;
  private attached = false;

  constructor(
    private readonly input: Phaser.Input.InputPlugin,
    private readonly bus: ActionBus,
    inputCfg: InputConfig,
    private readonly camCfg: CameraConfig,
    private readonly dragCapture: DragCapture = () => false,
    private readonly boxCapture: BoxCapture = () => false,
  ) {
    this.gesture = new PointerGesture(inputCfg.dragThreshold);
    this.touchThreshold = inputCfg.touchDragThreshold;
  }

  /** Last known MOUSE / pen position over the canvas, or null (outside, or using touch — no hover). */
  get position(): ScreenPoint | null {
    return this.lastPosition;
  }

  get isDragging(): boolean {
    return this.gesture.isDragging;
  }

  get isPinching(): boolean {
    return this.pinch.active;
  }

  /** Idempotent: calling twice never registers handlers twice. */
  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.input.mouse?.disableContextMenu();
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
    this.input.on(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
    this.input.on(Phaser.Input.Events.POINTER_UP, this.onUp, this);
    this.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onUpOutside, this);
    this.input.on(Phaser.Input.Events.GAME_OUT, this.onOut, this);
    this.input.on(Phaser.Input.Events.POINTER_WHEEL, this.onWheel, this);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.input.off(Phaser.Input.Events.POINTER_DOWN, this.onDown, this);
    this.input.off(Phaser.Input.Events.POINTER_MOVE, this.onMove, this);
    this.input.off(Phaser.Input.Events.POINTER_UP, this.onUp, this);
    this.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onUpOutside, this);
    this.input.off(Phaser.Input.Events.GAME_OUT, this.onOut, this);
    this.input.off(Phaser.Input.Events.POINTER_WHEEL, this.onWheel, this);
    this.gesture.cancel();
    this.pinch.reset();
    this.touches.clear();
    this.suppressUntilAllUp = false;
    this.activePointerId = null;
  }

  // ------------------------------------------------------------------

  private onDown(p: Phaser.Input.Pointer): void {
    const kind = kindOf(p);
    if (kind === 'touch') {
      this.touches.add(p.id);
      if (this.suppressUntilAllUp) return;
      if (this.pinch.down(p.id, { x: p.x, y: p.y })) {
        // Second finger: the first finger's tap / pan / preview drag is cancelled, never completed.
        if (this.boxing && this.gesture.isDragging) this.bus.emit({ type: 'boxSelect', from: this.boxFrom, to: this.boxFrom, done: true });
        this.gesture.cancel();
        this.activePointerId = null;
        this.grabbedPreview = false;
        this.boxing = false;
        return;
      }
    }
    if (this.activePointerId !== null) return;
    this.activePointerId = p.id;
    this.activeKind = kind;
    this.grabbedPreview = kind === 'touch' && this.dragCapture({ x: p.x, y: p.y });
    const shift = kind !== 'touch' && !!(p.event as MouseEvent | undefined)?.shiftKey;
    this.boxing = !this.grabbedPreview && p.button === 0 && this.boxCapture(shift);
    this.boxFrom = { x: p.x, y: p.y };
    if (kind !== 'touch') this.lastPosition = { x: p.x, y: p.y };
    if (kind === 'touch') this.gesture.down(p.x, p.y, this.touchThreshold);
    else this.gesture.down(p.x, p.y);
  }

  private onMove(p: Phaser.Input.Pointer): void {
    const kind = kindOf(p);
    if (kind === 'touch') {
      if (this.suppressUntilAllUp) return;
      const step = this.pinch.move(p.id, { x: p.x, y: p.y });
      if (step) this.bus.emit({ type: 'pinch', factor: step.factor, from: step.from, to: step.to });
      if (this.pinch.active) return;
    } else {
      this.lastPosition = { x: p.x, y: p.y };
    }
    if (this.activePointerId !== null && p.id !== this.activePointerId) return;
    if (this.gesture.isActive && p.isDown) {
      const d = this.gesture.move(p.x, p.y);
      if (this.grabbedPreview) {
        if (this.gesture.isDragging) this.bus.emit({ type: 'placementDrag', screen: { x: p.x, y: p.y } });
      } else if (this.boxing) {
        if (this.gesture.isDragging) this.bus.emit({ type: 'boxSelect', from: this.boxFrom, to: { x: p.x, y: p.y }, done: false });
      } else if (d.dx !== 0 || d.dy !== 0) {
        this.bus.emit({ type: 'pan', dx: d.dx, dy: d.dy });
      }
    }
    if (kind !== 'touch') this.bus.emit({ type: 'hover', screen: this.lastPosition });
  }

  private onUp(p: Phaser.Input.Pointer): void {
    if (kindOf(p) === 'touch' && this.releaseTouch(p.id)) return;
    if (p.id !== this.activePointerId) return;
    this.activePointerId = null;
    this.grabbedPreview = false;
    const wasBox = this.boxing && this.gesture.isDragging;
    this.boxing = false;
    const wasClick = this.gesture.up();
    if (wasBox) this.bus.emit({ type: 'boxSelect', from: this.boxFrom, to: { x: p.x, y: p.y }, done: true });
    // Left button (or a touch, which reports button 0) is primary; right button is secondary.
    if (!wasClick) return;
    if (p.button === 0) this.bus.emit({ type: 'primary', screen: { x: p.x, y: p.y }, pointer: this.activeKind });
    else if (p.button === 2) this.bus.emit({ type: 'secondary', screen: { x: p.x, y: p.y } });
  }

  private onUpOutside(p: Phaser.Input.Pointer): void {
    if (kindOf(p) === 'touch' && this.releaseTouch(p.id)) return;
    if (p.id !== this.activePointerId) return;
    this.activePointerId = null;
    this.grabbedPreview = false;
    if (this.boxing && this.gesture.isDragging) this.bus.emit({ type: 'boxSelect', from: this.boxFrom, to: { x: p.x, y: p.y }, done: true });
    this.boxing = false;
    this.gesture.cancel();
  }

  /**
   * Bookkeeping for a lifted finger. Returns true when the release must not
   * produce a tap (it ended a pinch, or we are waiting for all fingers up).
   */
  private releaseTouch(id: number): boolean {
    this.touches.delete(id);
    const endedPinch = this.pinch.up(id);
    if (endedPinch) {
      this.suppressUntilAllUp = this.touches.size > 0;
      this.pinch.reset();
      this.gesture.cancel();
      this.activePointerId = null;
      return true;
    }
    this.pinch.up(id);
    if (this.suppressUntilAllUp) {
      if (this.touches.size === 0) this.suppressUntilAllUp = false;
      return true;
    }
    return false;
  }

  private onOut(): void {
    this.lastPosition = null;
    this.bus.emit({ type: 'hover', screen: null });
  }

  private onWheel(p: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number): void {
    if (dy === 0) return;
    this.lastPosition = { x: p.x, y: p.y };
    // Normalise trackpads (many small deltas) and wheels (±100 per notch) to the same feel.
    const notches = Phaser.Math.Clamp(dy / 100, -3, 3);
    this.bus.emit({ type: 'zoom', factor: Math.pow(this.camCfg.zoomStep, -notches), screen: { x: p.x, y: p.y } });
  }
}

function kindOf(p: Phaser.Input.Pointer): PointerKind {
  if (p.wasTouch) return 'touch';
  const t = (p.event as PointerEvent | undefined)?.pointerType;
  return t === 'pen' ? 'pen' : 'mouse';
}
